import { Component, inject, OnDestroy } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Chart, ChartData, ChartOptions, Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { TravelBoardData } from 'src/app/shared/model/travel-board-data';
import { TravelBoardService } from '../../services/travel-board.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { ChartFilterComponent } from '../chart-filter/chart-filter.component';
import { LucideAngularModule } from 'lucide-angular';
import { ChartModule } from 'primeng/chart';
import { LockedButtonComponent } from 'src/app/shared/components/locked-button/locked-button.component';

@Component({
  selector: 'app-emission-per-trip-chart',
  templateUrl: './emission-per-trip-chart.component.html',
  imports: [
    TranslocoPipe,
    ChartFilterComponent,
    LucideAngularModule,
    ChartModule,
    LockedButtonComponent,
  ],
})
export class EmissionPerTripChartComponent implements OnDestroy {
  private translocoService = inject(TranslocoService);
  private travelBoardService = inject(TravelBoardService);
  protected packLimitationService = inject(PackLimitationsService);
  private currentUserService = inject(CurrentUserService);
  private formatCo2e = inject(FormatCo2eService);
  graphComparison = LimitedFeatures.GraphComparison;
  data: ChartData | undefined;
  options: ChartOptions | undefined;
  graphHeight: number = 500;
  plugins: Plugin[] = [ChartDataLabels];
  displayGraph = false;
  displayedCount = 0;
  totalCount = 0;
  private currentTrips: TravelBoardData[] = [];
  private averageValue: number = 0;
  private averageLabel: string = '';
  private objectiveValue: number | null = null;
  private objectiveLabel: string = '';
  private destroy$ = new Subject<void>();
  private tooltipLabels = {
    impact: '',
    travellers: '',
    duration: '',
    durationPlural: '',
    ecoScore: '',
  };

  private readonly DISPLAYED_ELEMENTS = 10;
  private readonly TITLE_LINE_LENGTH = 25;
  private readonly TITLE_MAX_LINES = 2;
  private readonly BAR_HEIGHT = 55;

  // Couleurs du projet
  private readonly COLOR_DEFAULT = '#DEE9E5'; // primary-100 (vert clair, comme les cercles d'icônes)
  private readonly COLOR_HOVER = '#93B5A8'; // primary-300 (vert foncé au hover)

  private chartTitle: string = '';

  // Plugin pour le titre aligné avec le début des barres
  private createTitlePlugin(): Plugin {
    return {
      id: 'customTitle',
      afterDraw: (chart: Chart) => {
        if (!this.chartTitle) return;

        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        if (!xScale || !yScale) return;

        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = '#575D57'; // greyscale-800
        ctx.font = '600 16px "Source Sans 3", sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(this.chartTitle, xScale.left, yScale.top - 25);
        ctx.restore();
      },
    };
  }

  // Plugin pour la ligne de moyenne
  private createAverageLinePlugin(): Plugin {
    return {
      id: 'averageLine',
      afterDatasetsDraw: (chart: Chart) => {
        if (this.averageValue === 0) return;

        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        if (!xScale || !yScale) return;

        const x = xScale.getPixelForValue(this.averageValue);
        const ctx = chart.ctx;

        ctx.save();
        ctx.strokeStyle = '#575D57'; // greyscale-800 (comme le titre)
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        // Ligne qui déborde vers le haut (au-dessus du chart)
        ctx.moveTo(x, yScale.top - 20);
        ctx.lineTo(x, yScale.bottom);
        ctx.stroke();

        // Label moyenne en bas de la ligne
        ctx.fillStyle = '#575D57'; // greyscale-800, même que datalabels
        ctx.font = '500 13px "Source Sans 3", sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(
          `${this.averageLabel}: ${this.formatValues(this.averageValue)}`,
          x + 6,
          yScale.bottom + 15,
        );
        ctx.restore();
      },
    };
  }
  // Plugin for the objective line (replaces average line when objective is set)
  private createObjectiveLinePlugin(): Plugin {
    return {
      id: 'objectiveLine',
      afterDatasetsDraw: (chart: Chart) => {
        if (this.objectiveValue === null || this.objectiveValue === 0) return;

        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        if (!xScale || !yScale) return;

        const x = xScale.getPixelForValue(this.objectiveValue);
        const ctx = chart.ctx;

        ctx.save();
        ctx.strokeStyle = '#2D6A4F'; // primary-600 (green, distinct from average)
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(x, yScale.top - 20);
        ctx.lineTo(x, yScale.bottom);
        ctx.stroke();

        // Label above the chart
        ctx.fillStyle = '#2D6A4F'; // primary-600
        ctx.font = '500 13px "Source Sans 3", sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(
          `${this.objectiveLabel}: ${this.formatValues(this.objectiveValue)}`,
          x + 6,
          yScale.top - 8,
        );
        ctx.restore();
      },
    };
  }

  private getChartOptions(): ChartOptions {
    return {
      indexAxis: 'y',
      maintainAspectRatio: false,
      onClick: (_event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const trip = this.currentTrips[index];
          if (trip?.id) {
            if (trip.isDraft) {
              this.travelBoardService.editTrip(trip.id);
            } else {
              this.travelBoardService.viewTrip(trip.id);
            }
          }
        }
      },
      plugins: {
        legend: { display: false },
        title: {
          display: false, // Désactivé, on utilise le plugin custom
        },
        tooltip: {
          enabled: true,
          backgroundColor: '#FFFFFF',
          titleColor: '#2A2D2A',
          bodyColor: '#575D57',
          borderColor: '#E3E7E3',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
          titleFont: {
            family: 'Source Sans 3, sans-serif',
            size: 14,
            weight: 600,
          },
          bodyFont: {
            family: 'Source Sans 3, sans-serif',
            size: 13,
          },
          callbacks: {
            title: (items) => {
              const trip = this.currentTrips[items[0].dataIndex];
              return trip?.title || '';
            },
            label: (item) => {
              const trip = this.currentTrips[item.dataIndex];
              if (!trip) return '';
              const impactValue = trip.impactPerTraveller ?? 0;
              const travellersCount = trip.travellers ?? 0;
              const daysCount = trip.days ?? 0;
              const lines = [
                this.tooltipLabels.impact.replace(
                  '%value%',
                  this.formatValues(impactValue),
                ),
                this.tooltipLabels.travellers.replace(
                  '%count%',
                  travellersCount.toString(),
                ),
                (daysCount > 1
                  ? this.tooltipLabels.durationPlural
                  : this.tooltipLabels.duration
                ).replace('%count%', daysCount.toString()),
              ];
              if (trip.ecoScore && trip.ecoScore > 0) {
                const scoreStr = Number.isInteger(trip.ecoScore)
                  ? trip.ecoScore.toString()
                  : trip.ecoScore.toFixed(1).replace('.', ',');
                lines.push(
                  this.tooltipLabels.ecoScore.replace('%score%', scoreStr),
                );
              }
              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          position: 'top',
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
          title: { display: false },
        },
        y: {
          ticks: {
            color: '#575D57',
            crossAlign: 'near', // Aligner à droite (près des barres)
            font: {
              family: "'Source Sans 3', sans-serif",
              size: 13,
              weight: 500,
            },
          },
          grid: { display: false },
          border: { display: false },
          title: { display: false },
        },
      },
      layout: {
        padding: {
          top: 40,
          right: 90,
          bottom: 20,
        },
      },
      onHover: (event, elements) => {
        const canvas = event.native?.target as HTMLCanvasElement;
        if (canvas) {
          canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      },
    };
  }

  private formatValues = (value: number | undefined | null): string => {
    return this.formatCo2e.format(value ?? 0);
  };
  private formatTitleForChart(title: string): string[] {
    // Si le titre tient sur une ligne, retourner tel quel
    if (title.length <= this.TITLE_LINE_LENGTH) {
      return [title];
    }

    // Sinon, découper en lignes
    const words = title.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;

      if (testLine.length <= this.TITLE_LINE_LENGTH) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;

        // Si on atteint le max de lignes, truncate le reste
        if (lines.length >= this.TITLE_MAX_LINES - 1) {
          const remaining = words.slice(words.indexOf(word)).join(' ');
          if (remaining.length > this.TITLE_LINE_LENGTH) {
            lines.push(remaining.slice(0, this.TITLE_LINE_LENGTH - 3) + '...');
          } else {
            lines.push(remaining);
          }
          return lines;
        }
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private updateChartData(data: TravelBoardData[]) {
    this.totalCount = data.length;
    const trimmedData = data.slice(0, this.DISPLAYED_ELEMENTS);
    this.displayedCount = trimmedData.length;
    this.currentTrips = trimmedData;
    this.graphHeight = trimmedData.length * this.BAR_HEIGHT + 50;

    // Calculer les valeurs d'émissions pour le graphe (10 premiers)
    const values = trimmedData.map((d) => d.impactPerTraveller ?? 0);

    // Calculer la moyenne sur les mêmes données que la card overview
    // (tous les voyages filtrés, pas juste les 10 affichés)
    const totalEmissions = data.reduce(
      (sum, t) => sum + (t.totalImpact || 0),
      0,
    );
    const totalTravelers = data.reduce(
      (sum, t) => sum + (t.travellers || 0),
      0,
    );
    this.averageValue =
      totalTravelers > 0 ? totalEmissions / totalTravelers : 0;

    // Les plugins seront mis à jour dans le subscribe après chargement des traductions

    // Check for active objective
    const avgObjective = this.currentUserService
      .objectives()
      .find((o) => o.type === 'AVG_EMISSION_PER_TRAVELLER' && o.isActive);
    this.objectiveValue = avgObjective ? avgObjective.targetValue : null;

    combineLatest([
      this.translocoService.selectTranslate(
        'travelBoards.tripsImpactPerTraveller',
      ),
      this.translocoService.selectTranslate('travelBoards.avgPerTraveller'),
      this.translocoService.selectTranslate('travelBoards.chart.tooltipImpact'),
      this.translocoService.selectTranslate(
        'travelBoards.chart.tooltipTravellers',
      ),
      this.translocoService.selectTranslate(
        'travelBoards.chart.tooltipDuration',
      ),
      this.translocoService.selectTranslate(
        'travelBoards.chart.tooltipDurationPlural',
      ),
      this.translocoService.selectTranslate(
        'travelBoards.chart.tooltipEcoScore',
      ),
      this.translocoService.selectTranslate('objectives.objectivePerTraveller'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([
          tripsImpactLabel,
          avgLabel,
          tooltipImpact,
          tooltipTravellers,
          tooltipDuration,
          tooltipDurationPlural,
          tooltipEcoScore,
          objectiveLabel,
        ]) => {
          this.averageLabel = avgLabel;
          this.objectiveLabel = objectiveLabel;
          this.chartTitle = tripsImpactLabel;
          this.tooltipLabels = {
            impact: tooltipImpact,
            travellers: tooltipTravellers,
            duration: tooltipDuration,
            durationPlural: tooltipDurationPlural,
            ecoScore: tooltipEcoScore,
          };

          this.plugins = [
            ChartDataLabels,
            this.createTitlePlugin(),
            this.createAverageLinePlugin(),
            ...(this.objectiveValue !== null
              ? [this.createObjectiveLinePlugin()]
              : []),
          ];

          this.data = {
            labels: trimmedData.map((d) => this.formatTitleForChart(d.title)),
            datasets: [
              {
                barPercentage: 1.15,
                label: tripsImpactLabel,
                backgroundColor: this.COLOR_DEFAULT,
                hoverBackgroundColor: this.COLOR_HOVER,
                borderColor: this.COLOR_DEFAULT,
                hoverBorderColor: this.COLOR_HOVER,
                barThickness: 40,
                data: values,
                datalabels: {
                  formatter: (value: number) => this.formatValues(value),
                  anchor: 'end',
                  align: 'end',
                  color: '#575D57',
                  font: {
                    family: "'Source Sans 3', sans-serif",
                    size: 13,
                    weight: 500,
                  },
                },
              },
            ],
          };

          this.options = this.getChartOptions();
          this.displayGraph = true;
        },
        error: (err) => console.error('Failed to load chart translations', err),
      });
  }
  public updateFilteredTrips(sortedTrips: TravelBoardData[]) {
    this.updateChartData(sortedTrips);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
